import {
    type ReactNode,
    useState
} from "react";
import {
    StyleSheet,
    View
} from "react-native";
import {
    ComponentHeader,
    AnatomySection,
    ExampleSection,
    getSectionToc,
    PropsTable
} from "./shared";
import {
    NCoreUIKitLocalize,
    NCoreUIKitTheme,
    formatDateTime,
    ToggleGroup,
    LineChart,
    AreaChart,
    BarChart,
    Button,
    Switch,
    Chart
} from "ncore-ui-kit";
import {
    useDocsI18n
} from "../../i18n";
import type {
    DocsTranslate
} from "../../anatomy/types";
import type {
    ChartTooltipBehaviour,
    ChartLegendPosition,
    ChartDataRow,
    ChartConfig
} from "ncore-ui-kit";
import {
    BulletList,
    InlineCode,
    Paragraph,
    CodeBlock,
    DocsTable,
    Callout,
    Article,
    Preview,
    Section
} from "../../components/content";

const NAME = "Chart";

const USAGE_CODE = `<Chart
    config={{
        visitors: {
            title: "Visitors",
            colorIndex: 1
        }
    }}
    description="Weekly visitors, last 12 weeks"
    note="Source: web analytics"
    title="Visitors"
>
    <Chart.Range
        options={[
            {
                title: "7 days",
                key: "7d"
            },
            {
                title: "12 weeks",
                key: "12w"
            }
        ]}
        onChange={setRange}
        value={range}
    />
    <Chart.Value
        description="vs previous 12 weeks"
        delta={0.125}
        value="3,335"
    />
    <LineChart
        categoryKey="week"
        data={data}
    />
</Chart>`;

const LEGEND_CODE = `<Chart ...>
    <Chart.Legend
        position="top"
    />
    <AreaChart
        stackBehaviour="stacked"
        categoryKey="month"
        data={data}
    />
</Chart>`;

const COLOR_CODE = `<Chart
    config={{
        thisYear: {
            title: "This year",
            colorIndex: 1
        },
        lastYear: {
            title: "Last year",
            colorIndex: "muted"
        }
    }}
    ...
>`;

const MOTION_CODE = `<Chart
    isAnimatedOnMount={true}
    key={replayKey}
    ...
>
    <BarChart
        categoryKey="month"
        data={data}
    />
</Chart>`;

const TOOLTIP_CODE = `<Chart
    tooltipBehaviour="pinned"
    ...
>
    <LineChart
        categoryKey="month"
        data={data}
    />
</Chart>`;

const STATES_CODE = `<Chart
    onStateAction={refetch}
    dataState="error"
    isLoading={false}
    ...
>`;

const CHART_SIZES = [
    {
        value: "small"
    },
    {
        value: "medium"
    }
] as const;

const PLOT_SIZES = [
    {
        value: "small"
    },
    {
        value: "medium"
    },
    {
        value: "large"
    }
] as const;

const RANGES = [
    {
        titleKey: "chartPage.range7d",
        key: "7d"
    },
    {
        titleKey: "chartPage.range30d",
        key: "30d"
    },
    {
        titleKey: "chartPage.range12w",
        key: "12w"
    }
] as const;

const LEGEND_POSITIONS = [
    {
        titleKey: "chartPage.legendBottom",
        value: "bottom"
    },
    {
        titleKey: "chartPage.legendTop",
        value: "top"
    }
] as const;

const TOOLTIP_BEHAVIOURS = [
    {
        value: "auto"
    },
    {
        value: "follow"
    },
    {
        value: "pinned"
    }
] as const;

const DATA_STATES = [
    {
        titleKey: "chartPage.stateReady",
        value: "ready"
    },
    {
        titleKey: "chartPage.stateLoading",
        value: "loading"
    },
    {
        titleKey: "chartPage.stateRefreshing",
        value: "refreshing"
    },
    {
        titleKey: "chartPage.stateEmpty",
        value: "empty"
    },
    {
        titleKey: "chartPage.stateError",
        value: "error"
    },
    {
        titleKey: "chartPage.stateDenied",
        value: "denied"
    },
    {
        titleKey: "chartPage.stateTable",
        value: "table"
    }
] as const;

const WEEKLY = [
    186,
    305,
    237,
    273,
    209,
    214,
    268,
    312,
    290,
    341,
    328,
    372
];

const DAILY_WEEK = [
    412,
    468,
    455,
    501,
    489,
    376,
    342
];

const DAILY_MONTH = Array.from({
    length: 30
}, (_, index) => Math.round(380 + 60 * Math.sin(index / 3) + index * 2 - (index % 7 > 4 ? 70 : 0)));

const DESKTOP = [
    18420,
    17960,
    21080,
    20350,
    22140,
    23010,
    21870,
    22960,
    24120,
    23540,
    25010,
    26340
];

const MOBILE = [
    21030,
    22480,
    25710,
    24900,
    26850,
    28120,
    27440,
    29010,
    30260,
    29780,
    31500,
    33120
];

const TABLET = [
    4210,
    4090,
    4650,
    4480,
    4720,
    4890,
    4610,
    4780,
    5010,
    4930,
    5120,
    5340
];

const THIS_YEAR = [
    812,
    846,
    901,
    873,
    938,
    972,
    1004,
    1021,
    1046,
    1088,
    1102,
    1160
];

const LAST_YEAR = [
    744,
    781,
    812,
    806,
    859,
    893,
    921,
    950,
    982,
    1004,
    1021,
    1068
];

const MONTHLY_VISITORS = [
    48200,
    52100,
    49800,
    56400,
    61200,
    64900
];

const RANGE_DELTAS = {
    "30d": -0.034,
    "12w": 0.125,
    "7d": 0.062
};

const DEVICE_KEYS = [
    "desktop",
    "mobile",
    "tablet"
];

const YEAR_KEYS = [
    "thisYear",
    "lastYear"
];

type RangeType = (typeof RANGES)[number]["key"];

type ChartSizeType = (typeof CHART_SIZES)[number]["value"];

type PlotSizeType = (typeof PLOT_SIZES)[number]["value"];

type DataStateType = (typeof DATA_STATES)[number]["value"];

type ToggleItemType = {
    titleKey?: string;
    value: string;
};

const stylesheet = StyleSheet.create({
    box: {
        width: "100%"
    }
});

const ChartBox = ({
    children
}: {
    children: ReactNode;
}) => {
    const {
        shapeConfigs
    } = NCoreUIKitTheme.useContext();

    return <View
        style={[
            stylesheet.box,
            {
                maxWidth: shapeConfigs.contentMaxWidth * 2 / 3
            }
        ]}
    >
        {children}
    </View>;
};

export const getToc = (t: DocsTranslate) => getSectionToc([
    {
        title: t("section.overview"),
        id: "overview"
    },
    {
        title: t("chartPage.partsTitle"),
        id: "parts"
    },
    {
        title: t("chartPage.legendTitle"),
        id: "legend"
    },
    {
        title: t("chartPage.colorTitle"),
        id: "colors"
    },
    {
        title: t("chartPage.motionTitle"),
        id: "motion"
    },
    {
        title: t("chartPage.tooltipTitle"),
        id: "tooltip"
    },
    {
        title: t("section.states"),
        id: "states"
    },
    {
        title: t("section.sizes"),
        id: "sizes"
    },
    {
        title: t("section.accessibility"),
        id: "accessibility"
    },
    {
        title: t("section.props"),
        id: "props"
    }
], NAME, t);

const ChartPage = () => {
    const {
        t
    } = useDocsI18n();

    const {
        activeLocale
    } = NCoreUIKitLocalize.useContext();

    const [
        range,
        setRange
    ] = useState<RangeType>("12w");

    const [
        legendPosition,
        setLegendPosition
    ] = useState<ChartLegendPosition>("bottom");

    const [
        isPatterned,
        setIsPatterned
    ] = useState(false);

    const [
        isAnimated,
        setIsAnimated
    ] = useState(true);

    const [
        replayKey,
        setReplayKey
    ] = useState(0);

    const [
        tooltipBehaviour,
        setTooltipBehaviour
    ] = useState<ChartTooltipBehaviour>("auto");

    const [
        dataState,
        setDataState
    ] = useState<DataStateType>("ready");

    const [
        size,
        setSize
    ] = useState<ChartSizeType>("medium");

    const [
        plotSize,
        setPlotSize
    ] = useState<PlotSizeType>("medium");

    const getDate = (date: Date, options: Intl.DateTimeFormatOptions) => formatDateTime({
        locale: activeLocale,
        options,
        date
    });

    const formatNumber = (value: number) => new Intl.NumberFormat(activeLocale).format(value);

    const getRangeRows = (): Array<ChartDataRow> => {
        const dayOptions = {
            month: "short",
            day: "numeric"
        } as const;

        if(range === "7d") {
            return DAILY_WEEK.map((visitors, index) => ({
                label: getDate(new Date(2026, 9, 25 + index), dayOptions),
                visitors
            }));
        }

        if(range === "30d") {
            return DAILY_MONTH.map((visitors, index) => ({
                label: getDate(new Date(2026, 9, 2 + index), dayOptions),
                visitors
            }));
        }

        return WEEKLY.map((visitors, index) => ({
            label: getDate(new Date(2026, 7, 5 + index * 7), dayOptions),
            visitors
        }));
    };

    const getMonthLabels = (year: number, count: number, month: "short" | "long" = "short") => Array.from({
        length: count
    }, (_, index) => getDate(new Date(year, index, 1), {
        month
    }));

    const rangeRows = getRangeRows();

    const rangeTotal = rangeRows.reduce((total, row) => total + Number(row.visitors), 0);

    const deviceRows: Array<ChartDataRow> = getMonthLabels(2025, 12).map((month, index) => ({
        desktop: DESKTOP[index] ?? 0,
        mobile: MOBILE[index] ?? 0,
        tablet: TABLET[index] ?? 0,
        month
    }));

    const yearRows: Array<ChartDataRow> = getMonthLabels(2026, 12).map((month, index) => ({
        lastYear: LAST_YEAR[index] ?? 0,
        thisYear: THIS_YEAR[index] ?? 0,
        month
    }));

    const stateRows: Array<ChartDataRow> = Array.from({
        length: 6
    }, (_, index) => ({
        month: getDate(new Date(2026, 4 + index, 1), {
            month: "short"
        }),
        visitors: MONTHLY_VISITORS[index] ?? 0
    }));

    const visitorsConfig: ChartConfig = {
        visitors: {
            title: t("chartPage.visitors"),
            colorIndex: 1
        }
    };

    const deviceConfig: ChartConfig = {
        desktop: {
            title: t("chartPage.desktop"),
            colorIndex: 1
        },
        mobile: {
            title: t("chartPage.mobile"),
            colorIndex: 2
        },
        tablet: {
            title: t("chartPage.tablet"),
            colorIndex: 3
        }
    };

    const yearConfig: ChartConfig = {
        lastYear: {
            title: t("chartPage.lastYear"),
            colorIndex: "muted"
        },
        thisYear: {
            title: t("chartPage.thisYear"),
            colorIndex: 1
        }
    };

    const rangeOptions = RANGES.map((item) => ({
        title: t(item.titleKey),
        key: item.key
    }));

    const handleRangeChange = (key: string) => {
        const nextRange = RANGES.find((item) => item.key === key);

        if(nextRange) {
            setRange(nextRange.key);
        }
    };

    const handleLegendPositionChange = (value: string | Array<string>) => {
        const next = LEGEND_POSITIONS.find((item) => item.value === value);

        if(next) {
            setLegendPosition(next.value);
        }
    };

    const handleTooltipChange = (value: string | Array<string>) => {
        const next = TOOLTIP_BEHAVIOURS.find((item) => item.value === value);

        if(next) {
            setTooltipBehaviour(next.value);
        }
    };

    const handleSizeChange = (value: string | Array<string>) => {
        const nextSize = CHART_SIZES.find((item) => item.value === value);

        if(nextSize) {
            setSize(nextSize.value);
        }
    };

    const handlePlotSizeChange = (value: string | Array<string>) => {
        const nextPlotSize = PLOT_SIZES.find((item) => item.value === value);

        if(nextPlotSize) {
            setPlotSize(nextPlotSize.value);
        }
    };

    const handleStateChange = (value: string | Array<string>) => {
        const nextState = DATA_STATES.find((item) => item.value === value);

        if(nextState) {
            setDataState(nextState.value);
        }
    };

    const handleTableVisibleChange = (isTableVisible: boolean) => setDataState(isTableVisible ? "table" : "ready");

    const handleReplay = () => setReplayKey((previous) => previous + 1);

    const code = (value: string) => <InlineCode>
        {value}
    </InlineCode>;

    const renderToggleGroup = (title: string, items: ReadonlyArray<ToggleItemType>, value: string, onChange: (value: string | Array<string>) => void) => <ToggleGroup
        attachBehaviour="attached"
        onValueChange={onChange}
        isClearEnabled={false}
        variant="outline"
        title={title}
        value={value}
        size="small"
    >
        {
            items.map((item) => <ToggleGroup.Item
                title={item.titleKey ? t(item.titleKey) : item.value}
                value={item.value}
                key={item.value}
            />)
        }
    </ToggleGroup>;

    const getStateProps = () => {
        if(dataState === "empty" || dataState === "error" || dataState === "denied") {
            return {
                stateDescription: t(`chartPage.${dataState}Description`),
                stateActionTitle: t(`chartPage.${dataState}Action`),
                stateTitle: t(`chartPage.${dataState}Title`),
                onStateAction: () => setDataState("ready"),
                dataState
            };
        }

        return {
            isLoading: dataState === "loading" || dataState === "refreshing"
        };
    };

    return <Article>
        <ComponentHeader
            lead={t("chartPage.lead")}
            name={NAME}
        />
        <Section
            title={t("section.overview")}
            id="overview"
        >
            <Paragraph>
                {t("chartPage.overviewText")}
            </Paragraph>
            <Preview
                caption={t("chartPage.overviewCaption")}
            >
                <ChartBox>
                    <Chart
                        description={t("chartPage.visitorsDescription")}
                        note={t("chartPage.visitorsNote")}
                        title={t("chartPage.visitors")}
                        spreadBehaviour="stretch"
                        config={visitorsConfig}
                    >
                        <Chart.Range
                            onChange={handleRangeChange}
                            options={rangeOptions}
                            value={range}
                        />
                        <Chart.Value
                            description={t("chartPage.valueDescription")}
                            value={formatNumber(rangeTotal)}
                            delta={RANGE_DELTAS[range]}
                        />
                        <LineChart
                            categoryTitle={t("chartPage.date")}
                            categoryKey="label"
                            data={rangeRows}
                        />
                    </Chart>
                </ChartBox>
            </Preview>
            <CodeBlock
                code={USAGE_CODE}
                language="tsx"
            />
        </Section>
        <AnatomySection
            name={NAME}
        />
        <Section
            title={t("chartPage.partsTitle")}
            id="parts"
        >
            <Paragraph>
                {t("chartPage.partsText")}
            </Paragraph>
            <DocsTable
                rows={[
                    [
                        code("Chart.Value"),
                        code("value, delta, deltaFormatter, isDeltaInverted, description"),
                        t("chartPage.partValue")
                    ],
                    [
                        code("Chart.Range"),
                        code("options, value, onChange, title, isDisabled"),
                        t("chartPage.partRange")
                    ],
                    [
                        code("Chart.Legend"),
                        code("position, items, hiddenKeys, onToggleSeries, isToggleable"),
                        t("chartPage.partLegend")
                    ],
                    [
                        code("Chart.TableToggle"),
                        code("isTableVisible, onToggle, isDisabled"),
                        t("chartPage.partTableToggle")
                    ]
                ]}
                headers={[
                    t("chartPage.partsHeaderName"),
                    t("chartPage.partsHeaderProps"),
                    t("props.description")
                ]}
            />
            <Callout>
                {t("chartPage.partsCallout")}
            </Callout>
        </Section>
        <Section
            title={t("chartPage.legendTitle")}
            id="legend"
        >
            <Paragraph>
                {t("chartPage.legendText")}
            </Paragraph>
            <Preview
                caption={t("chartPage.legendCaption")}
            >
                {renderToggleGroup("position", LEGEND_POSITIONS, legendPosition, handleLegendPositionChange)}
                <ChartBox>
                    <Chart
                        description={t("chartPage.deviceDescription")}
                        title={t("chartPage.deviceTitle")}
                        spreadBehaviour="stretch"
                        config={deviceConfig}
                    >
                        <Chart.Legend
                            position={legendPosition}
                        />
                        <AreaChart
                            categoryTitle={t("chartPage.month")}
                            stackBehaviour="stacked"
                            seriesKeys={DEVICE_KEYS}
                            categoryKey="month"
                            data={deviceRows}
                            plotSize="small"
                        />
                    </Chart>
                </ChartBox>
            </Preview>
            <CodeBlock
                code={LEGEND_CODE}
                language="tsx"
            />
        </Section>
        <Section
            title={t("chartPage.colorTitle")}
            id="colors"
        >
            <Paragraph>
                {t("chartPage.colorText")}
            </Paragraph>
            <Preview
                caption={t("chartPage.colorCaption")}
            >
                <Switch
                    onCheckedChange={setIsPatterned}
                    isChecked={isPatterned}
                    title="isPatterned"
                />
                <ChartBox>
                    <Chart
                        description={t("chartPage.ordersDescription")}
                        title={t("chartPage.ordersTitle")}
                        isPatterned={isPatterned}
                        spreadBehaviour="stretch"
                        config={yearConfig}
                    >
                        <BarChart
                            categoryTitle={t("chartPage.month")}
                            seriesKeys={YEAR_KEYS}
                            categoryKey="month"
                            plotSize="small"
                            data={yearRows}
                        />
                    </Chart>
                </ChartBox>
            </Preview>
            <CodeBlock
                code={COLOR_CODE}
                language="tsx"
            />
        </Section>
        <Section
            title={t("chartPage.motionTitle")}
            id="motion"
        >
            <Paragraph>
                {t("chartPage.motionText")}
            </Paragraph>
            <Preview
                caption={t("chartPage.motionCaption")}
            >
                <Switch
                    onCheckedChange={setIsAnimated}
                    title="isAnimatedOnMount"
                    isChecked={isAnimated}
                />
                <Button
                    title={t("chartPage.replay")}
                    onPress={handleReplay}
                    variant="outline"
                    type="neutral"
                    size="small"
                />
                <ChartBox>
                    <Chart
                        description={t("chartPage.ordersDescription")}
                        title={t("chartPage.ordersTitle")}
                        key={`${replayKey}-${isAnimated}`}
                        isAnimatedOnMount={isAnimated}
                        spreadBehaviour="stretch"
                        config={yearConfig}
                    >
                        <BarChart
                            categoryTitle={t("chartPage.month")}
                            seriesKeys={YEAR_KEYS}
                            categoryKey="month"
                            plotSize="small"
                            data={yearRows}
                        />
                    </Chart>
                </ChartBox>
            </Preview>
            <CodeBlock
                code={MOTION_CODE}
                language="tsx"
            />
            <Callout>
                {t("chartPage.motionCallout")}
            </Callout>
        </Section>
        <Section
            title={t("chartPage.tooltipTitle")}
            id="tooltip"
        >
            <Paragraph>
                {t("chartPage.tooltipText")}
            </Paragraph>
            <Preview
                caption={t("chartPage.tooltipCaption")}
            >
                {renderToggleGroup("tooltipBehaviour", TOOLTIP_BEHAVIOURS, tooltipBehaviour, handleTooltipChange)}
                <ChartBox>
                    <Chart
                        description={t("chartPage.visitsDescription")}
                        tooltipBehaviour={tooltipBehaviour}
                        title={t("chartPage.visitsTitle")}
                        spreadBehaviour="stretch"
                        config={yearConfig}
                    >
                        <LineChart
                            categoryTitle={t("chartPage.month")}
                            seriesKeys={YEAR_KEYS}
                            categoryKey="month"
                            plotSize="small"
                            data={yearRows}
                        />
                    </Chart>
                </ChartBox>
            </Preview>
            <CodeBlock
                code={TOOLTIP_CODE}
                language="tsx"
            />
            <Callout>
                {t("chartPage.tooltipCallout")}
            </Callout>
        </Section>
        <Section
            title={t("section.states")}
            id="states"
        >
            <Paragraph>
                {t("chartPage.statesText")}
            </Paragraph>
            <Preview
                caption={t("chartPage.statesCaption")}
            >
                {renderToggleGroup(t("section.states"), DATA_STATES, dataState, handleStateChange)}
                <ChartBox>
                    <Chart
                        {...getStateProps()}
                        description={t("chartPage.stateChartDescription")}
                        onTableVisibleChange={handleTableVisibleChange}
                        isTableVisible={dataState === "table"}
                        title={t("chartPage.stateChartTitle")}
                        note={t("chartPage.stateChartNote")}
                        spreadBehaviour="stretch"
                        config={visitorsConfig}
                    >
                        <LineChart
                            data={dataState === "loading" ? [] : stateRows}
                            categoryTitle={t("chartPage.month")}
                            categoryKey="month"
                            plotSize="small"
                        />
                    </Chart>
                </ChartBox>
            </Preview>
            <CodeBlock
                code={STATES_CODE}
                language="tsx"
            />
        </Section>
        <Section
            title={t("section.sizes")}
            id="sizes"
        >
            <Paragraph>
                {t("chartPage.sizesText")}
            </Paragraph>
            <Preview
                caption={t("chartPage.sizesCaption")}
            >
                {renderToggleGroup("size", CHART_SIZES, size, handleSizeChange)}
                {renderToggleGroup("plotSize", PLOT_SIZES, plotSize, handlePlotSizeChange)}
                <ChartBox>
                    <Chart
                        description={t("chartPage.ordersDescription")}
                        title={t("chartPage.ordersTitle")}
                        spreadBehaviour="stretch"
                        config={yearConfig}
                        size={size}
                    >
                        <BarChart
                            categoryTitle={t("chartPage.month")}
                            seriesKeys={YEAR_KEYS}
                            plotSize={plotSize}
                            categoryKey="month"
                            data={yearRows}
                        />
                    </Chart>
                </ChartBox>
            </Preview>
        </Section>
        <Section
            title={t("section.accessibility")}
            id="accessibility"
        >
            <BulletList
                items={[
                    t("chartPage.a11yCard"),
                    t("chartPage.a11yPlot"),
                    t("chartPage.a11yKeyboard"),
                    t("chartPage.a11yTouch"),
                    t("chartPage.a11yLegend"),
                    t("chartPage.a11yTable"),
                    t("chartPage.a11yColor"),
                    t("chartPage.a11yMotion"),
                    t("chartPage.a11yStates")
                ]}
            />
        </Section>
        <Section
            title={t("section.props")}
            id="props"
        >
            <PropsTable
                rows={[
                    {
                        description: t("chartPage.propTitle"),
                        type: "string",
                        name: "title"
                    },
                    {
                        type: "Record<string, { title: string; colorIndex: ChartColorIndex; icon?: NCoreUIKitIcon }>",
                        description: t("chartPage.propConfig"),
                        name: "config"
                    },
                    {
                        description: t("chartPage.propDescription"),
                        name: "description",
                        type: "string"
                    },
                    {
                        description: t("chartPage.propNote"),
                        type: "string",
                        name: "note"
                    },
                    {
                        description: t("chartPage.propFooter"),
                        type: "ReactNode",
                        name: "footer"
                    },
                    {
                        description: t("chartPage.propSize"),
                        type: "\"small\" | \"medium\"",
                        defaultValue: "\"medium\"",
                        name: "size"
                    },
                    {
                        description: t("chartPage.propSpreadBehaviour"),
                        type: "\"baseline\" | \"stretch\"",
                        defaultValue: "\"baseline\"",
                        name: "spreadBehaviour"
                    },
                    {
                        description: t("chartPage.propIsAnimatedOnMount"),
                        name: "isAnimatedOnMount",
                        defaultValue: "false",
                        type: "boolean"
                    },
                    {
                        description: t("chartPage.propTooltipBehaviour"),
                        type: "\"auto\" | \"follow\" | \"pinned\"",
                        name: "tooltipBehaviour",
                        defaultValue: "\"auto\""
                    },
                    {
                        description: t("chartPage.propIsLegendToggleable"),
                        name: "isLegendToggleable",
                        defaultValue: "true",
                        type: "boolean"
                    },
                    {
                        description: t("chartPage.propIsPatterned"),
                        defaultValue: "false",
                        name: "isPatterned",
                        type: "boolean"
                    },
                    {
                        description: t("chartPage.propIsTableVisible"),
                        name: "isTableVisible",
                        type: "boolean"
                    },
                    {
                        description: t("chartPage.propOnTableVisibleChange"),
                        type: "(isTableVisible: boolean) => void",
                        name: "onTableVisibleChange"
                    },
                    {
                        type: "\"ready\" | \"empty\" | \"error\" | \"denied\"",
                        description: t("chartPage.propDataState"),
                        defaultValue: "\"ready\"",
                        name: "dataState"
                    },
                    {
                        description: t("chartPage.propIsLoading"),
                        defaultValue: "false",
                        name: "isLoading",
                        type: "boolean"
                    },
                    {
                        description: t("chartPage.propStateTitle"),
                        name: "stateTitle",
                        type: "string"
                    },
                    {
                        description: t("chartPage.propStateDescription"),
                        name: "stateDescription",
                        type: "string"
                    },
                    {
                        description: t("chartPage.propStateActionTitle"),
                        name: "stateActionTitle",
                        type: "string"
                    },
                    {
                        description: t("chartPage.propOnStateAction"),
                        name: "onStateAction",
                        type: "() => void"
                    },
                    {
                        type: "{ themeKey, paletteKey, sharpness, gapPropagation }",
                        description: t("chartPage.propCustomTheme"),
                        name: "customTheme"
                    },
                    {
                        description: t("chartPage.propCustomLocalize"),
                        type: "{ activeLocale }",
                        name: "customLocalize"
                    },
                    {
                        description: t("chartPage.propChildren"),
                        type: "ReactNode",
                        name: "children"
                    }
                ]}
            />
            <Paragraph
                isMuted={true}
            >
                {t("chartPage.propsNote")}
            </Paragraph>
            <Paragraph>
                {t("chartPage.partsListTitle")}
            </Paragraph>
            <DocsTable
                rows={[
                    [
                        code("Chart.Header"),
                        code("title, description, value, actions, isRefetching, isTableToggleVisible"),
                        t("chartPage.partHeader")
                    ],
                    [
                        code("Chart.Content"),
                        code("children, value, legendTop, legendBottom, stateTitle, stateDescription, stateActionTitle, onStateAction"),
                        t("chartPage.partContent")
                    ],
                    [
                        code("Chart.Footer"),
                        code("note, children, isRefetching, isTableToggleVisible, isDivided"),
                        t("chartPage.partFooter")
                    ],
                    [
                        code("Chart.Skeleton"),
                        code("kind, plotSize, height"),
                        t("chartPage.partSkeleton")
                    ],
                    [
                        code("Chart.Table"),
                        code("columns, rows, caption"),
                        t("chartPage.partTable")
                    ],
                    [
                        code("Chart.Tooltip"),
                        code("title, rows, sum, children"),
                        t("chartPage.partTooltip")
                    ],
                    [
                        code("Chart.Plot"),
                        code("children, pointCount, seriesCount, summary, getAnchor, getIndex, getTooltip, getReading, plotSize, tooltipBehaviour, renderTooltip, onActiveChange, activeIndex"),
                        t("chartPage.partPlot")
                    ],
                    [
                        code("Chart.Axis"),
                        code("plot, ticks, orientation, tone, isRTL"),
                        t("chartPage.partAxis")
                    ],
                    [
                        code("Chart.Grid"),
                        code("plot, positions, orientation, baseline"),
                        t("chartPage.partGrid")
                    ]
                ]}
                headers={[
                    t("chartPage.partsHeaderName"),
                    t("chartPage.partsHeaderProps"),
                    t("props.description")
                ]}
            />
        </Section>
        <ExampleSection
            name={NAME}
        />
    </Article>;
};
export default ChartPage;
